import { CONTENT_VERSION } from "@/constants/cmsFallback";

/**
 * CMS Resilience — configuration.
 *
 * ALL tunable thresholds for the resilience layer live here. Values may be
 * overridden at runtime via environment variables (CRA reads REACT_APP_*).
 * The frontend business logic never hardcodes any of these numbers.
 *
 *   REACT_APP_CMS_RETRY_DELAYS_MS   — comma-separated, e.g. "300,900,2500"
 *   REACT_APP_CMS_BREAKER_THRESHOLD — consecutive failures to open breaker
 *   REACT_APP_CMS_BREAKER_TIMEOUT_MS — how long breaker stays open
 *   REACT_APP_CMS_CACHE_TTL_MS      — cache entry lifetime (0 = never expire)
 *   REACT_APP_CMS_CACHE_NAMESPACE   — localStorage prefix (defaults to "mts")
 *   REACT_APP_CMS_HEALTH_PATH       — health endpoint path (defaults to /api/health/cms)
 *
 * Content version is NOT env-configurable: it is baked into the JS bundle by
 * the baseline generator so cache invalidation stays in lock-step with the
 * static fallback.
 */

const parseIntArray = (raw, fallback) => {
  if (!raw || typeof raw !== "string") return fallback;
  const arr = raw
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n >= 0);
  return arr.length ? arr : fallback;
};

const parseIntOr = (raw, fallback) => {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const env = (typeof process !== "undefined" && process.env) || {};

export const RESILIENCE_CONFIG = Object.freeze({
  // Retry policy for a single request. Length of array = number of retries
  // AFTER the first attempt. e.g. [300, 900, 2500] → up to 4 attempts total.
  retryDelaysMs: parseIntArray(env.REACT_APP_CMS_RETRY_DELAYS_MS, [300, 900, 2500]),

  // Circuit-breaker: after N consecutive failures, the breaker OPENS and
  // subsequent calls fail-fast for `breakerTimeoutMs` before HALF-OPEN.
  breakerThreshold: parseIntOr(env.REACT_APP_CMS_BREAKER_THRESHOLD, 3),
  breakerTimeoutMs: parseIntOr(env.REACT_APP_CMS_BREAKER_TIMEOUT_MS, 30_000),

  // Cache entry TTL in ms. 0 or negative = never expire (rely on version).
  cacheTtlMs: parseIntOr(env.REACT_APP_CMS_CACHE_TTL_MS, 15 * 60 * 1000),

  // localStorage namespace prefix.
  cacheNamespace: env.REACT_APP_CMS_CACHE_NAMESPACE || "mts",

  // Health probe endpoint (served by FastAPI).
  healthPath: env.REACT_APP_CMS_HEALTH_PATH || "/api/health/cms",

  // Build-time content version — auto-baked by the baseline generator from
  // /app/cms/src/seed-data.ts. Runtime uses this to invalidate caches when
  // content shape changes.
  contentVersion: CONTENT_VERSION,
});

export default RESILIENCE_CONFIG;
